import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import { LANG_OPTIONS } from "../i18n/translations";
import { ChevronDown } from "lucide-react";
import Logo from "./Logo";

function Dropdown({ label, items, onSelect, value, testid }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    // pointerdown covers both mouse and touch (mousedown misses taps on
    // non-interactive elements on iOS Safari)
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button
        data-testid={testid}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="flex items-center gap-1 text-sm tracking-wide hover:text-eduhu-gold transition-colors"
      >
        {label} <ChevronDown size={14} strokeWidth={1.5} aria-hidden="true" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 min-w-[140px] bg-white border border-eduhu-border z-50 shadow-xl"
        >
          {items.map((it) => (
            <button
              key={it.value}
              role="menuitem"
              data-testid={`${testid}-opt-${it.value}`}
              onClick={() => {
                onSelect(it.value);
                setOpen(false);
              }}
              className={`block w-full text-left px-4 py-2 text-sm hover:bg-eduhu-bg3 transition-colors ${
                value === it.value ? "text-eduhu-gold" : ""
              }`}
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const { user, logout } = useAuth();
  const { lang, setLang, currency, setCurrency, t } = useI18n();
  const navigate = useNavigate();

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 bg-eduhu-bg/85 backdrop-blur-xl border-b border-eduhu-border"
    >
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-12 h-16 sm:h-20 flex items-center justify-between gap-3 sm:gap-6">

        {/* Logo — icon only on mobile, full wordmark on sm+ */}
        <Link to="/" data-testid="logo-link" aria-label="EDUHUB — go to homepage" className="flex items-center group shrink-0">
          <Logo size={36} variant="dark" className="sm:hidden" />
          <Logo size={42} variant="dark" showWordmark className="hidden sm:inline-flex" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8 text-eduhu-ink" role="navigation" aria-label="Main navigation">
          <NavLink data-testid="nav-courses" to="/courses" className="nav-link text-sm">
            {t("nav.courses")}
          </NavLink>
          <NavLink data-testid="nav-playground" to="/playground" className="nav-link text-sm">
            {t("nav.playground")}
          </NavLink>
          <NavLink data-testid="nav-how" to="/#how" className="nav-link text-sm">
            {t("nav.howItWorks")}
          </NavLink>
          <NavLink data-testid="nav-about" to="/about" className="nav-link text-sm">
            {t("nav.about")}
          </NavLink>
          <NavLink data-testid="nav-blog" to="/blog" className="nav-link text-sm">
            Blog
          </NavLink>
          <NavLink data-testid="nav-corporate" to="/corporate" className="nav-link text-sm">
            Corporate
          </NavLink>
        </nav>

        <div className="flex items-center gap-2 sm:gap-4 lg:gap-6">
          <Dropdown
            testid="lang-dropdown"
            label={lang.toUpperCase()}
            value={lang}
            onSelect={setLang}
            items={LANG_OPTIONS.map((o) => ({
              value: o.value,
              label: `${o.flag} ${o.label}`,
            }))}
          />
          <Dropdown
            testid="currency-dropdown"
            label={currency}
            value={currency}
            onSelect={setCurrency}
            items={[
              { value: "USD", label: "USD — Dollar" },
              { value: "EUR", label: "EUR — Euro" },
              { value: "RON", label: "RON — Leu" },
            ]}
          />
          {user && user.role ? (
            <>
              {(user.role === "admin" || user.role === "sub_admin") && (
                <>
                  <Link to="/admin" data-testid="nav-admin" className="hidden md:inline-flex nav-link text-sm">
                    {t("nav.admin")}
                  </Link>
                  {(user.role === "admin" || (user.permissions || []).includes("support")) && (
                    <Link to="/admin/support" data-testid="nav-admin-support" className="hidden md:inline-flex nav-link text-sm">
                      Support
                    </Link>
                  )}
                </>
              )}
              <Link
                to={
                  user.role === "teacher"
                    ? "/teacher"
                    : user.role === "admin" || user.role === "sub_admin"
                    ? "/admin"
                    : "/dashboard"
                }
                data-testid="nav-dashboard"
                className="hidden md:inline-flex nav-link text-sm"
              >
                {t("nav.dashboard")}
              </Link>
              <Link
                to="/profile"
                data-testid="nav-profile"
                className="hidden md:inline-flex nav-link text-sm"
              >
                Profile
              </Link>
              <button
                data-testid="nav-signout"
                onClick={async () => {
                  await logout();
                  navigate("/");
                }}
                className="text-sm nav-link whitespace-nowrap"
              >
                {t("nav.signout")}
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="nav-signin"
                className="text-sm nav-link whitespace-nowrap"
              >
                {t("nav.signin")}
              </Link>
              <Link
                to="/register"
                data-testid="nav-register"
                className="eduhu-btn-gold !py-2 !px-3 sm:!px-4 text-sm whitespace-nowrap"
              >
                {t("nav.enroll")}
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
